import logo from "@/assets/favicon_io/android-chrome-192x192.png";
import { config } from "@/shared/lib/config";
import { formatDate, formatInLao, formatTime } from "@/shared/lib/date-time";
import {
    RECEIPT_LOGO_STYLE_48MM,
    RECEIPT_PRINT_STYLES_48MM,
    buildReceiptProfileSection,
    formatAssistInstructorBillHtml,
    formatBillAmount,
} from "@/shared/lib/receipt-print-48mm";
import QRCode from "qrcode";
import type { CreditInvoiceDetailDTO } from "../api/client";

function refTypeLabel(refType: string): string {
  switch (refType) {
    case "room_booking":
      return "ຈອງຫ້ອງ";
    case "package_purchase":
      return "ແພັກເກດຄລາດສ໌";
    case "private_lesson_package":
      return "ແພັກເກດຮຽນສ່ວນຕົວ";
    case "partner_project_sale":
      return "ຂາຍ Partner / ອອກສະຖານທີ່";
    default:
      return refType;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * ພິມໃບໃບເກັບເງິນທີ່ຊຳລະຄົບແລ້ວ (ບໍ່ມີ dialog)
 */
export async function printPaidCreditInvoice(
  invoice: CreditInvoiceDetailDTO,
): Promise<void> {
  const paymentsChrono = [...invoice.payments]
    .filter((p) => p.status === "active")
    .sort((a, b) => a.paidAt.localeCompare(b.paidAt));

  const origin = window.location.origin;
  let profileSection = "";
  if (invoice.studentMemberId) {
    const profileUrl = `${origin}/public/student/${encodeURIComponent(invoice.studentMemberId)}`;
    const qrDataUrl = await QRCode.toDataURL(profileUrl, {
      width: 100,
      margin: 1,
    });
    profileSection = buildReceiptProfileSection(
      invoice.studentMemberId,
      profileUrl,
      qrDataUrl,
    );
  }

  const logoBase64 = await new Promise<string>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } else resolve("");
    };
    img.onerror = () => resolve("");
    img.src = logo;
  });

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const invShort = invoice.id.slice(0, 8).toUpperCase();
  const dateTime = formatInLao(new Date().toISOString(), "dd/MM/yyyy HH:mm");
  const issuedFmt = formatInLao(invoice.issuedAt, "dd/MM/yyyy HH:mm");
  const address = config.brandAddress ?? "VIENTIANE CENTER, 4th floor";
  const addressLo = config.brandAddressLo ?? "ວຽງຈັນເຊັນເຕີ, ຊັ້ນ4";
  const tagline = config.billFooterTagline ?? "Keep Dancing, Keep Moving!";
  const logoImg =
    logoBase64 &&
    `<img src="${logoBase64}" alt="Logo" style="${RECEIPT_LOGO_STYLE_48MM}" />`;

  let contextLines = "";
  if (invoice.refType === "room_booking") {
    const contact = [invoice.contactName, invoice.contactPhone]
      .filter(Boolean)
      .join(" · ");
    if (contact) {
      contextLines += `<div class="bill-item-line">ຜູ້ຕິດຕໍ່: ${escapeHtml(contact)}</div>`;
    }
    contextLines += `<div class="bill-item-line">ຫ້ອງ: ${escapeHtml(invoice.roomName ?? "—")}</div>`;
    const when =
      invoice.bookingDate != null
        ? `${formatDate(invoice.bookingDate)}${
            invoice.bookingStartTime && invoice.bookingEndTime
              ? ` · ${formatTime(invoice.bookingStartTime)} – ${formatTime(invoice.bookingEndTime)}`
              : ""
          }`
        : "—";
    contextLines += `<div class="bill-item-line">ວັນທີ / ເວລາ: ${escapeHtml(when)}</div>`;
    contextLines += formatAssistInstructorBillHtml(invoice.assistInstructorNames);
  } else {
    contextLines += `<div class="bill-item-line">ນັກຮຽນ: ${escapeHtml(invoice.studentName ?? "—")}</div>`;
    if (invoice.studentMemberId) {
      contextLines += `<div class="bill-item-line">ເລກສະມາຊິກ: ${escapeHtml(invoice.studentMemberId)}</div>`;
    }
    contextLines += `<div class="bill-item-line">ແພັກເກດ: ${escapeHtml(invoice.packageContextLabel ?? "—")}</div>`;
  }

  const paymentLines = paymentsChrono
    .map(
      (p) =>
        `<div class="bill-item-line">${formatInLao(p.paidAt, "dd/MM/yyyy HH:mm")} · ${formatBillAmount(p.amount)} ກີບ</div>`,
    )
    .join("");

  const notesBlock =
    invoice.notes && invoice.notes.trim() !== ""
      ? `<div class="bill-line">ໝາຍເຫດ: ${escapeHtml(invoice.notes.trim())}</div>`
      : "";

  const content = `
    <div class="bill-header">
      ${logoImg ?? ""}
      <div class="bill-brand">${config.brandName.toUpperCase()}</div>
      <div class="bill-address">${addressLo}</div>
      <div class="bill-address">${address}</div>
    </div>
    <hr class="bill-sep" />
    <div class="bill-line" style="font-weight:700">ໃບຢັ້ງຢືນຊຳລະແລ້ວ</div>
    <div class="bill-line">ເລກໃບເກັບເງິນ: ${invShort}</div>
    <div class="bill-line">ປະເພດ: ${escapeHtml(refTypeLabel(invoice.refType))}</div>
    <div class="bill-line">ອອກໃບເມື່ອ: ${issuedFmt}</div>
    <div class="bill-line">ພິມເມື່ອ: ${dateTime}</div>
    <hr class="bill-sep" />
    <div class="bill-items">${contextLines}</div>
    <hr class="bill-sep" />
    ${notesBlock ? `${notesBlock}<hr class="bill-sep" />` : ""}
    <div class="bill-total-section">
      <div class="bill-total-label">ຍອດລວມ</div>
      <div class="bill-total-amount">${formatBillAmount(invoice.totalAmount)} ກີບ</div>
    </div>
    <div class="bill-line" style="margin-top:2mm;font-weight:600">ຊຳລະຄົບແລ້ວ: ${formatBillAmount(invoice.paidAmount)} ກີບ</div>
    <hr class="bill-sep" />
    <div class="bill-line" style="font-weight:600">ລາຍການຮັບເງິນ</div>
    <div class="bill-items">${paymentLines}</div>
    <hr class="bill-sep" />
    ${profileSection}
    ${profileSection ? '<hr class="bill-sep" />' : ""}
    <div class="bill-footer">
      <div class="bill-footer-tagline">${escapeHtml(tagline)}</div>
      <div class="bill-footer-thanks">ຂອບໃຈທີ່ໃຊ້ບໍລິການ</div>
    </div>
  `;

  printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ບິນຊຳລະແລ້ວ - ${config.brandName}</title>
          <style>${RECEIPT_PRINT_STYLES_48MM}</style>
        </head>
        <body>${content}</body>
      </html>
    `);
  printWindow.document.close();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}
